import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { authApi, ApiError } from '../api/client';
import { QRCodeSVG } from 'qrcode.react';

export function LoginPage() {
    const navigate = useNavigate();
    const { setUser } = useAuthStore();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');

    // 状态管理：login | 2fa | setup
    const [step, setStep] = useState<'login' | '2fa' | 'setup'>('login');
    const [totpSecret, setTotpSecret] = useState(''); // 仅用于 setup

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (step === 'login') {
                await handleLogin();
            } else if (step === '2fa') {
                await handleLoginWith2FA();
            } else if (step === 'setup') {
                await handleSetup2FA();
            }
        } catch (err) {
            if (err instanceof ApiError) {
                // 检查是否需要 2FA 流程
                if (err.data?.requires2FA) {
                    if (step === '2fa') {
                        // 已经在 2FA 步骤，显示验证错误
                        setError(err.message || '验证码错误');
                    } else {
                        // 首次进入 2FA 步骤
                        setStep('2fa');
                        setError('');
                    }
                } else if (err.data?.setup2FA) {
                    setStep('setup');
                    setTotpSecret(err.data.tempToken);
                    setError('');
                } else {
                    setError(err.message);
                }
            } else {
                setError('操作失败，请重试');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async () => {
        const { user, sessionId } = await authApi.login(username, password);
        // 如果没有抛出需要 2FA 的异常，说明登录成功
        setUser(user, sessionId);
        navigate('/dashboard');
    };

    const handleLoginWith2FA = async () => {
        const { user, sessionId } = await authApi.login(username, password, totpCode);
        setUser(user, sessionId);
        navigate('/dashboard');
    };

    const handleSetup2FA = async () => {
        const { user, sessionId } = await authApi.bind2FA(username, password, totpCode, totpSecret);
        setUser(user, sessionId);
        navigate('/dashboard');
    };

    return (
        <div>
            <h2 className="text-center mb-lg" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-semibold)' }}>
                {step === 'setup' ? '绑定两步验证' : '登录到 GitCenter'}
            </h2>

            <form onSubmit={handleSubmit}>
                {error && (
                    <div className="mb-md" style={{
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        backgroundColor: 'rgba(248, 81, 73, 0.1)',
                        border: '1px solid var(--color-danger)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-danger)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        {error}
                    </div>
                )}

                {/* 登录表单 */}
                {step === 'login' && (
                    <>
                        <div className="form-group">
                            <label className="form-label" htmlFor="username">
                                用户名或邮箱
                            </label>
                            <input
                                id="username"
                                type="text"
                                className="form-input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                autoComplete="username"
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="password">
                                密码
                            </label>
                            <input
                                id="password"
                                type="password"
                                className="form-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                            />
                        </div>
                    </>
                )}

                {/* 2FA 验证表单 */}
                {step === '2fa' && (
                    <div className="form-group">
                        <label className="form-label" htmlFor="totpCode">
                            两步验证码
                        </label>
                        <input
                            id="totpCode"
                            type="text"
                            className="form-input"
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value)}
                            required
                            placeholder="请输入 6 位验证码"
                            autoFocus
                            autoComplete="one-time-code"
                        />
                        <p className="text-muted mt-sm" style={{ fontSize: 'var(--font-size-sm)' }}>
                            请打开 Google Authenticator app 查看验证码
                        </p>
                    </div>
                )}

                {/* 2FA 设置表单 */}
                {step === 'setup' && (
                    <div className="text-center">
                        <div className="mb-md" style={{ padding: '20px', background: 'white', display: 'inline-block', borderRadius: '8px' }}>
                            <QRCodeSVG
                                value={`otpauth://totp/GitCenter:${username}?secret=${totpSecret}&issuer=GitCenter`}
                                size={200}
                            />
                        </div>
                        <p className="mb-sm">请使用 Google Authenticator 扫描上方二维码</p>

                        {/* 显示密钥供用户备份 */}
                        <div className="mb-md" style={{
                            padding: 'var(--spacing-sm) var(--spacing-md)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <p className="text-muted mb-xs" style={{ fontSize: '12px' }}>密钥（请妥善保存）：</p>
                            <code style={{
                                fontFamily: 'monospace',
                                letterSpacing: '2px',
                                wordBreak: 'break-all',
                                userSelect: 'all',
                                cursor: 'text'
                            }}>
                                {totpSecret}
                            </code>
                        </div>

                        <div className="form-group text-left">
                            <label className="form-label" htmlFor="setupCode">
                                验证码
                            </label>
                            <input
                                id="setupCode"
                                type="text"
                                className="form-input"
                                value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value)}
                                required
                                placeholder="扫描后输入 6 位验证码"
                            />
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 'var(--spacing-md)' }}
                    disabled={loading}
                >
                    {loading ? '处理中...' : (step === 'login' ? '下一步' : '验证并登录')}
                </button>

                {step !== 'login' && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ width: '100%', marginTop: 'var(--spacing-sm)' }}
                        onClick={() => {
                            setStep('login');
                            setTotpCode('');
                            setError('');
                        }}
                    >
                        返回登录
                    </button>
                )}
            </form>

            {step === 'login' && (
                <p className="text-center mt-lg text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
                    如需创建账户，请联系管理员
                </p>
            )}
        </div>
    );
}
